/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableSort, type TableSortValue } from "@src/shared/constants/sort-order.constants";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { appNotificationService } from "@src/@api-services/app-notification";
import type {
  AddAppNotification,
  IAppNotification,
  IAppNotificationLog,
  IAppNotificationLogSearchOptions,
  IAppNotificationSearchOptions,
} from "../types";

interface AppNotificationState {
  data: IAppNotification[];
  total: number;
  params: IAppNotificationSearchOptions;
  selectedNotification: IAppNotification | null;
  logs: IAppNotificationLog[];
  logsTotal: number;
  logsParams: IAppNotificationLogSearchOptions;
  selectedLog: IAppNotificationLog | null;
  loading: boolean;
  detailLoading: boolean;
  logsLoading: boolean;
  logDetailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getAppNotifications = createAsyncThunk(
  "appNotifications/getAppNotifications",
  async (params: IAppNotificationSearchOptions, { rejectWithValue }) => {
    try {
      const response = await appNotificationService.getAppNotifications(params);
      return {
        params,
        data: response.data.data.data as IAppNotification[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

export const getAppNotificationDetails = createAsyncThunk(
  "appNotifications/getAppNotificationDetails",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response =
        await appNotificationService.getAppNotificationDetails(id);
      return {
        ...(response.data.data as IAppNotification),
        id: Number(id),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification details"
      );
    }
  }
);

export const addAppNotification = createAsyncThunk(
  "appNotifications/addAppNotification",
  async (data: AddAppNotification, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await appNotificationService.addAppNotification(data);
      const state = getState() as any;
      await dispatch(getAppNotifications(state.appNotifications.params));
      const created = response.data?.data ?? response.data ?? {};
      return {
        ...(typeof created === "object" ? created : {}),
        id: Number(created?.id),
      } as IAppNotification;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add notification"
      );
    }
  }
);

export const updateAppNotification = createAsyncThunk(
  "appNotifications/updateAppNotification",
  async (
    { id, data }: { id: string | number; data: Partial<AddAppNotification> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await appNotificationService.updateAppNotification(
        id,
        data
      );
      const state = getState() as any;
      await dispatch(getAppNotifications(state.appNotifications.params));
      return response.data.data as IAppNotification;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update notification"
      );
    }
  }
);

export const deleteAppNotification = createAsyncThunk(
  "appNotifications/deleteAppNotification",
  async (id: string | number, { dispatch, getState, rejectWithValue }) => {
    try {
      await appNotificationService.deleteAppNotification(id);
      const state = getState() as any;
      await dispatch(getAppNotifications(state.appNotifications.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete notification"
      );
    }
  }
);

export const sendAppNotification = createAsyncThunk(
  "appNotifications/sendAppNotification",
  async (id: string | number, { dispatch, rejectWithValue }) => {
    try {
      const response = await appNotificationService.sendAppNotification(id);
      await dispatch(getAppNotificationDetails(id));
      return response.data.data as {
        id: number;
        status: string;
        totalRecipients: number;
        successCount: number;
        failureCount: number;
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send notification"
      );
    }
  }
);

export const getAppNotificationLogs = createAsyncThunk(
  "appNotifications/getAppNotificationLogs",
  async (params: IAppNotificationLogSearchOptions, { rejectWithValue }) => {
    try {
      const response =
        await appNotificationService.getAppNotificationLogs(params);
      return {
        params,
        data: response.data.data.data as IAppNotificationLog[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification logs"
      );
    }
  }
);

export const getLogsForAppNotification = createAsyncThunk(
  "appNotifications/getLogsForAppNotification",
  async (
    {
      notificationId,
      params,
    }: {
      notificationId: string | number;
      params?: Omit<IAppNotificationLogSearchOptions, "notificationId">;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await appNotificationService.getLogsForAppNotification(
        notificationId,
        params
      );
      return {
        params: { ...params, notificationId },
        data: response.data.data.data as IAppNotificationLog[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification logs"
      );
    }
  }
);

export const getAppNotificationLogDetails = createAsyncThunk(
  "appNotifications/getAppNotificationLogDetails",
  async (logId: string | number, { rejectWithValue }) => {
    try {
      const response =
        await appNotificationService.getAppNotificationLogDetails(logId);
      return {
        ...(response.data.data as IAppNotificationLog),
        id: Number(logId),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification log"
      );
    }
  }
);

const initialState: AppNotificationState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedNotification: null,
  logs: [],
  logsTotal: 0,
  logsParams: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedLog: null,
  loading: false,
  detailLoading: false,
  logsLoading: false,
  logDetailLoading: false,
  actionLoading: false,
  error: null,
};

export const appNotificationSlice = createSlice({
  name: "appNotifications",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedNotification: (state) => {
      state.selectedNotification = null;
    },
    clearSelectedLog: (state) => {
      state.selectedLog = null;
    },
    clearNotificationLogs: (state) => {
      state.logs = [];
      state.logsTotal = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAppNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAppNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getAppNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getAppNotificationDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getAppNotificationDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedNotification = action.payload;
      })
      .addCase(getAppNotificationDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedNotification = null;
      })
      .addCase(addAppNotification.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addAppNotification.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addAppNotification.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppNotification.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateAppNotification.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updateAppNotification.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppNotification.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteAppNotification.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deleteAppNotification.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(sendAppNotification.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(sendAppNotification.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(sendAppNotification.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(getAppNotificationLogs.pending, (state) => {
        state.logsLoading = true;
        state.error = null;
      })
      .addCase(getAppNotificationLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload.data;
        state.logsTotal = action.payload.total;
        state.logsParams = action.payload.params;
      })
      .addCase(getAppNotificationLogs.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload as string;
        state.logs = [];
        state.logsTotal = 0;
      })
      .addCase(getLogsForAppNotification.pending, (state) => {
        state.logsLoading = true;
        state.error = null;
      })
      .addCase(getLogsForAppNotification.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.logs = action.payload.data;
        state.logsTotal = action.payload.total;
        state.logsParams = action.payload.params;
      })
      .addCase(getLogsForAppNotification.rejected, (state, action) => {
        state.logsLoading = false;
        state.error = action.payload as string;
        state.logs = [];
        state.logsTotal = 0;
      })
      .addCase(getAppNotificationLogDetails.pending, (state) => {
        state.logDetailLoading = true;
        state.error = null;
      })
      .addCase(getAppNotificationLogDetails.fulfilled, (state, action) => {
        state.logDetailLoading = false;
        state.selectedLog = action.payload;
      })
      .addCase(getAppNotificationLogDetails.rejected, (state) => {
        state.logDetailLoading = false;
        state.selectedLog = null;
      });
  },
});

export const {
  clearError,
  clearSelectedNotification,
  clearSelectedLog,
  clearNotificationLogs,
} = appNotificationSlice.actions;

export default appNotificationSlice.reducer;
